import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUser, faPhone, faCar } from '@fortawesome/free-solid-svg-icons';
import styles from './ContactModal.module.css';

const ContactModal = ({ isOpen, onClose, onSave, editingContact = null }) => {
  const [name, setName] = useState(editingContact?.name || '');
  const [phone, setPhone] = useState(editingContact?.phone || '');
  const [role, setRole] = useState(editingContact?.role || 'guide');
  const [customRole, setCustomRole] = useState('');
  const [vehicleName, setVehicleName] = useState(editingContact?.vehicleDetails?.name || '');
  const [vehicleColor, setVehicleColor] = useState(editingContact?.vehicleDetails?.color || '');
  const [vehicleNumber, setVehicleNumber] = useState(editingContact?.vehicleDetails?.number || '');

  // Initialize custom role if editing and role is not predefined
  useEffect(() => {
    if (editingContact && !['guide', 'driver'].includes(editingContact.role)) {
      setRole('custom');
      setCustomRole(editingContact.role);
    }
  }, [editingContact]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!name.trim() || !phone.trim()) {
      alert('Name and phone are required');
      return;
    }

    if (role === 'custom' && !customRole.trim()) {
      alert('Please enter a custom role');
      return;
    }

    const finalRole = role === 'custom' ? customRole.trim() : role;

    const contactData = {
      id: editingContact ? editingContact.id : Date.now().toString(),
      name: name.trim(),
      phone: phone.trim(),
      role: finalRole,
      createdAt: editingContact ? editingContact.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Add vehicle details if role is driver
    if (finalRole === 'driver') {
      contactData.vehicleDetails = {
        name: vehicleName.trim(),
        color: vehicleColor.trim(),
        number: vehicleNumber.trim()
      };
    }

    onSave(contactData, !!editingContact);
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setPhone('');
    setRole('guide');
    setCustomRole('');
    setVehicleName('');
    setVehicleColor('');
    setVehicleNumber('');
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {editingContact ? 'Edit Contact' : 'Add Contact'}
          </h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="name">
              <FontAwesomeIcon icon={faUser} className={styles.fieldIcon} />
              Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter contact name"
              className={styles.formInput}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="phone">
              <FontAwesomeIcon icon={faPhone} className={styles.fieldIcon} />
              Phone
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter phone number"
              className={styles.formInput}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="role">Role</label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={styles.formSelect}
            >
              <option value="guide">Guide</option>
              <option value="driver">Driver</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {role === 'custom' && (
            <div className={styles.formGroup}>
              <label htmlFor="customRole">Custom Role</label>
              <input
                id="customRole"
                type="text"
                value={customRole}
                onChange={(e) => setCustomRole(e.target.value)}
                placeholder="Enter custom role (e.g., Security, Coordinator)"
                className={styles.formInput}
                required
              />
            </div>
          )}

          {(role === 'driver' || (role === 'custom' && customRole.toLowerCase() === 'driver')) && (
            <div className={styles.vehicleSection}>
              <h3 className={styles.vehicleTitle}>
                <FontAwesomeIcon icon={faCar} className={styles.fieldIcon} />
                Vehicle Details
              </h3>
              
              <div className={styles.formGroup}>
                <label htmlFor="vehicleName">Vehicle Name</label>
                <input
                  id="vehicleName"
                  type="text"
                  value={vehicleName}
                  onChange={(e) => setVehicleName(e.target.value)}
                  placeholder="e.g., Honda City, Toyota Innova"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="vehicleColor">Vehicle Color</label>
                <input
                  id="vehicleColor"
                  type="text"
                  value={vehicleColor}
                  onChange={(e) => setVehicleColor(e.target.value)}
                  placeholder="e.g., White, Black, Silver"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="vehicleNumber">Vehicle Number</label>
                <input
                  id="vehicleNumber"
                  type="text"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                  placeholder="e.g., MH 01 AB 1234"
                  className={styles.formInput}
                />
              </div>
            </div>
          )}

          <div className={styles.formActions}>
            <button
              type="button"
              onClick={handleClose}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={styles.submitButton}
            >
              {editingContact ? 'Update' : 'Add'} Contact
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;